import { useEffect, useRef } from 'react';
import clsx from 'clsx';
import { ActivityTimelineItem } from './activityHistory';

type Props = {
  timeline: ActivityTimelineItem[];
  maximumSpanLabel?: string | null;
  compact?: boolean;
  selectedMarkerNumbers: number[];
  scrollRequest: number;
  onSelectItem: (item: ActivityTimelineItem) => void;
};

const ActivityNumber = ({
  number,
  highlighted,
  muted
}: {
  number: number;
  highlighted: boolean;
  muted: boolean;
}) => (
  <span
    aria-hidden="true"
    className={clsx(
      'flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold',
      muted
        ? 'border border-dashed border-grey-400 text-grey-500'
        : highlighted
          ? 'bg-secondary-light text-white'
          : 'bg-grey-600 text-white'
    )}>
    {number}
  </span>
);

const ActivityHistoryLegend = ({
  timeline,
  maximumSpanLabel,
  compact = false,
  selectedMarkerNumbers,
  scrollRequest,
  onSelectItem
}: Props) => {
  const itemRefs = useRef(new Map<number, HTMLLIElement>());

  useEffect(() => {
    if (scrollRequest === 0 || selectedMarkerNumbers.length === 0) {
      return;
    }

    itemRefs.current
      .get(selectedMarkerNumbers[0])
      ?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
  }, [scrollRequest, selectedMarkerNumbers]);

  return (
    <aside
      aria-label="Document submission timeline"
      className={clsx(
        'z-10 bg-white',
        compact
          ? 'w-full border-t border-grey-200 md:absolute md:right-0 md:top-0 md:w-[360px] md:rounded-bl-lg md:border-b md:border-l md:border-t-0 md:shadow-xs'
          : 'w-full border-t border-grey-200 xl:w-[38%] xl:min-w-[320px] xl:border-l xl:border-t-0'
      )}>
      {maximumSpanLabel && (
        <div className="border-b border-grey-200 px-5 py-4">
          <p className="text-sm font-semibold text-grey-900">
            Max span between document uploads: {maximumSpanLabel}
          </p>
        </div>
      )}
      <ol className={clsx(!compact && 'max-h-[600px] overflow-y-auto')}>
        {timeline.map((item) => {
          const hasLocation = Boolean(item.location);
          const selected = selectedMarkerNumbers.includes(item.markerNumber);

          return (
            <li
              key={item.id}
              ref={(element) => {
                if (element) {
                  itemRefs.current.set(item.markerNumber, element);
                } else {
                  itemRefs.current.delete(item.markerNumber);
                }
              }}
              className="border-b border-grey-100 last:border-b-0">
              <button
                type="button"
                aria-pressed={hasLocation ? selected : undefined}
                disabled={!hasLocation}
                title={hasLocation ? undefined : 'No location captured'}
                onClick={() => onSelectItem(item)}
                className={clsx(
                  'flex min-h-[70px] w-full items-start gap-3 px-5 py-4 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-secondary-light',
                  hasLocation ? 'hover:bg-grey-50' : 'cursor-default',
                  selected && 'bg-secondary-light/10'
                )}>
                <ActivityNumber
                  number={item.markerNumber}
                  highlighted={item.markerTone === 'highlight'}
                  muted={!hasLocation}
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-grey-700">
                    {item.documentName}
                  </p>
                  <time
                    className="mt-0.5 block text-xs text-grey-500"
                    dateTime={item.submittedAt}>
                    {item.exactTimestamp}
                  </time>
                  {!hasLocation && (
                    <p className="mt-0.5 text-xs text-grey-400">
                      No location captured
                    </p>
                  )}
                </div>
                <span className="flex-shrink-0 pt-0.5 text-xs font-semibold text-grey-700">
                  {item.elapsedLabel}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </aside>
  );
};

export default ActivityHistoryLegend;
